import {
  type ChangePaymentMethodResponseType,
  type CreateCheckoutSessionRequestType,
  type CreateCheckoutSessionResponseType,
  type GetEligibleStoresResponseType,
  type GetInvoicesResponseType,
  type GetLicensesQueryType,
  type GetLicensesResponseType,
  type GetPublishersConnectionsSummaryType,
  type GetSelfServiceInfoResponse,
  type GetUpgradeablePackagesResponseType,
  type PreviewUpgradeRequestType,
  type PreviewUpgradeResponseType,
  type UpgradePackageRequestType,
  type UpgradePackageResponseType,
} from '@/services/api/premium/types'
import { type PremiumFeaturesReturnProps } from '@/services/types/premiumFeatures/premiumFeatures'
import { type AvailableProviders } from '@/utils/constants/providers/availableProviders'
import { api, v2BaseURL } from '../api'
import { endpoints } from '../endpoints'

export const premiumService = {
  getFeatures: async () =>
    api.get<PremiumFeaturesReturnProps>(endpoints.premium.getFeatures, {
      baseURL: v2BaseURL,
    }),

  getLicenses: async (props: GetLicensesQueryType) =>
    api.get<GetLicensesResponseType>(endpoints.premium.getLicenses, {
      params: props,
      baseURL: v2BaseURL,
    }),

  getInvoices: async (licenseId: string) =>
    api.get<GetInvoicesResponseType>(
      endpoints.premium.getInvoices.replace(':licenseId', licenseId),
      {
        baseURL: v2BaseURL,
      },
    ),

  getPublishersConnectionsSummary: async () =>
    api.get<GetPublishersConnectionsSummaryType>(endpoints.premium.publishersConnections.summary, {
      baseURL: v2BaseURL,
    }),

  getEligibleStores: async (provider: AvailableProviders) =>
    api.get<GetEligibleStoresResponseType>(endpoints.premium.publishersConnections.eligibleStores, {
      baseURL: v2BaseURL,
      params: { provider },
    }),

  getUpgradeablePackages: async () =>
    api.get<GetUpgradeablePackagesResponseType>(endpoints.premium.getUpgradeablePackages, {
      baseURL: v2BaseURL,
    }),

  previewUpgrade: async (data: PreviewUpgradeRequestType) =>
    api.post<PreviewUpgradeResponseType>(endpoints.premium.previewUpgrade, data, {
      baseURL: v2BaseURL,
    }),

  upgrade: async (data: UpgradePackageRequestType) =>
    api.post<UpgradePackageResponseType>(endpoints.premium.upgrade, data, {
      baseURL: v2BaseURL,
    }),

  createCheckoutSession: async (data: CreateCheckoutSessionRequestType) => {
    const { insertCoupon, ...rest } = data
    return api.post<CreateCheckoutSessionResponseType>(
      endpoints.premium.createCheckoutSession,
      rest,
      {
        baseURL: v2BaseURL,
        params: insertCoupon ? { insertCoupon: true } : undefined,
      },
    )
  },

  changePaymentMethod: async () =>
    api.post<ChangePaymentMethodResponseType>(endpoints.premium.changePaymentMethod, undefined, {
      baseURL: v2BaseURL,
    }),

  chooseFirstPackage: async () =>
    api.post<void>(endpoints.premium.chooseFirstPackage, undefined, {
      baseURL: v2BaseURL,
    }),

  getSelfServiceInfo: async () =>
    api.get<GetSelfServiceInfoResponse>(endpoints.premium.getSelfServiceInfo, {
      baseURL: v2BaseURL,
    }),
}
